import { useCallback, useEffect, useState } from "react";

import { useMe } from "./queries";
import { AccountType } from "../models/AccountData";
import {
  GetUserTermsConditionsResponse,
  TermsConditions
} from "../models/termsConditionsData";
import DataSvc from "../services/dataSvc";

interface UseTermsConditionsResult {
  needsAcceptance: boolean;
  latestTerms: TermsConditions | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

export const useTermsConditions = (): UseTermsConditionsResult => {
  const { data: me } = useMe();
  const [needsAcceptance, setNeedsAcceptance] = useState(false);
  const [latestTerms, setLatestTerms] = useState<TermsConditions | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const checkTermsConditions = useCallback(async () => {
    if (!me) return;

    // Skip terms and conditions check for Admin users
    if (me.type === AccountType.ADMIN) {
      setNeedsAcceptance(false);
      setLatestTerms(null);
      setIsLoading(false);
      setError(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response: GetUserTermsConditionsResponse =
        await DataSvc.getUserTermsConditions(Number(me.id).toString());

      setLatestTerms(response.latestTermsConditions);

      if (response.latestTermsConditions) {
        // Check if the user has signed the latest terms and conditions
        // userSignatureRecord will be null if not signed, or contain the signature record if signed
        const hasSignedLatest = response.userSignatureRecord !== null;

        setNeedsAcceptance(!hasSignedLatest);
      } else {
        // No terms and conditions exist, so no acceptance needed
        setNeedsAcceptance(false);
      }
    } catch (err) {
      console.error("Error checking terms and conditions:", err);
      setError("Failed to check terms and conditions");
      setNeedsAcceptance(false);
    } finally {
      setIsLoading(false);
    }
  }, [me]);

  useEffect(() => {
    if (me) {
      checkTermsConditions();
    }
  }, [me, checkTermsConditions]);

  return {
    needsAcceptance,
    latestTerms,
    isLoading,
    error,
    refetch: checkTermsConditions
  };
};
